import ActivityTimeline from '@/components/shared/ActivityTimeline'
import PipelineProgress from '@/components/shared/PipelineProgress'
import { formatCurrency, formatDate } from '@/lib/utils'
import {
  IconFileText, IconDollar, IconCheckCircle, IconXCircle,
  IconReceipt, IconClock, IconTarget,
} from '@/components/icons/Icons'
import type { Cliente, Cotizacion, Proyecto, Factura, PagoProgramado, EtapaPipeline } from '@/lib/types'

interface TabResumenProps {
  empresa: Cliente
  cotizaciones: Cotizacion[]
  proyectos: Proyecto[]
  facturas: Factura[]
  proximoPago: PagoProgramado | null
  etapaPipeline?: EtapaPipeline | null
  /**
   * Lo cobrado es lo que 1Conecta le facturo a esta empresa. En el espacio del
   * partner no se muestra: sin facturas la cifra saldria «USD 0.00», que no es
   * un dato ausente sino uno falso.
   */
  mostrarDinero?: boolean
}

function buildTimeline(
  facturas: Factura[],
  cotizaciones: Cotizacion[],
  proyectos: Proyecto[],
) {
  type TimelineItem = {
    id: string
    icon: React.ReactNode
    title: string
    description?: string
    time: string
    date: Date
    color: 'success' | 'info' | 'warning' | 'danger' | 'primary'
  }

  const items: TimelineItem[] = []

  facturas.forEach(f => {
    if (f.estado === 'pagada' && f.fecha_pago) {
      items.push({
        id: `f-paid-${f.id}`,
        icon: <IconDollar size={16} />,
        title: `Factura ${f.numero} pagada`,
        description: `${formatCurrency(f.total, f.moneda)}`,
        time: formatDate(f.fecha_pago),
        date: new Date(f.fecha_pago),
        color: 'success',
      })
    }
    if (f.fecha_emision) {
      items.push({
        id: `f-emit-${f.id}`,
        icon: <IconReceipt size={16} />,
        title: `Factura ${f.numero} emitida`,
        description: `${formatCurrency(f.total, f.moneda)}`,
        time: formatDate(f.fecha_emision),
        date: new Date(f.fecha_emision),
        color: 'info',
      })
    }
  })

  cotizaciones.forEach(c => {
    if (c.estado === 'aceptada') {
      items.push({
        id: `c-accept-${c.id}`,
        icon: <IconCheckCircle size={16} />,
        title: `Cotizacion ${c.numero} aceptada`,
        description: `${formatCurrency(c.total, c.moneda)}`,
        time: formatDate(c.created_at),
        date: new Date(c.created_at),
        color: 'success',
      })
    } else if (c.estado === 'rechazada') {
      items.push({
        id: `c-reject-${c.id}`,
        icon: <IconXCircle size={16} />,
        title: `Cotizacion ${c.numero} rechazada`,
        description: `${formatCurrency(c.total, c.moneda)}`,
        time: formatDate(c.created_at),
        date: new Date(c.created_at),
        color: 'danger',
      })
    } else {
      items.push({
        id: `c-created-${c.id}`,
        icon: <IconFileText size={16} />,
        title: `Cotizacion ${c.numero} creada`,
        description: `${c.proyecto_nombre || 'Sin proyecto'} - ${formatCurrency(c.total, c.moneda)}`,
        time: formatDate(c.created_at),
        date: new Date(c.created_at),
        color: 'primary',
      })
    }
  })

  proyectos.forEach(p => {
    if (p.estado === 'completado') {
      items.push({
        id: `p-done-${p.id}`,
        icon: <IconCheckCircle size={16} />,
        title: `Proyecto "${p.nombre}" completado`,
        time: formatDate(p.fecha_fin_estimada || p.created_at),
        date: new Date(p.fecha_fin_estimada || p.created_at),
        color: 'success',
      })
    } else {
      items.push({
        id: `p-created-${p.id}`,
        icon: <IconTarget size={16} />,
        title: `Proyecto "${p.nombre}" creado`,
        description: `Estado: ${p.estado}`,
        time: formatDate(p.created_at),
        date: new Date(p.created_at),
        color: 'info',
      })
    }
  })

  items.sort((a, b) => b.date.getTime() - a.date.getTime())
  return items.slice(0, 10)
}

export default function TabResumen({
  cotizaciones,
  proyectos,
  facturas,
  proximoPago,
  etapaPipeline,
  mostrarDinero = true,
}: TabResumenProps) {
  const timelineEvents = buildTimeline(facturas, cotizaciones, proyectos)

  const totalRevenue = facturas
    .filter(f => f.estado === 'pagada')
    .reduce((sum, f) => sum + f.total, 0)

  const totalCotizado = cotizaciones.reduce((sum, c) => sum + c.total, 0)
  const cotAceptadas = cotizaciones.filter(c => c.estado === 'aceptada').length

  return (
    <div className="empresa-tab-resumen">
      <div className="empresa-tab-resumen__main">
        <div className="empresa-section-card">
          <h4 className="empresa-section-title">Actividad Reciente</h4>
          {timelineEvents.length > 0 ? (
            <ActivityTimeline events={timelineEvents} />
          ) : (
            <p className="empresa-empty-text">Sin actividad registrada para esta empresa</p>
          )}
        </div>
      </div>

      <div className="empresa-tab-resumen__side">
        {etapaPipeline && (
          <div className="empresa-section-card">
            <h4 className="empresa-section-title">Pipeline</h4>
            <PipelineProgress currentStage={etapaPipeline} showLabels />
          </div>
        )}

        {proximoPago && (
          <div className="empresa-next-payment">
            <div className="empresa-next-payment__header">
              <IconClock size={16} />
              <span>Proximo Pago</span>
            </div>
            <div className="empresa-next-payment__body">
              <span className="empresa-next-payment__amount">
                {formatCurrency(proximoPago.monto, proximoPago.moneda)}
              </span>
              <span className="empresa-next-payment__date">
                Vence: {formatDate(proximoPago.fecha_vencimiento)}
              </span>
              <span className="empresa-next-payment__desc">{proximoPago.descripcion}</span>
            </div>
          </div>
        )}

        <div className="empresa-section-card">
          <h4 className="empresa-section-title">Resumen Rapido</h4>
          <div className="empresa-quick-stats">
            {mostrarDinero && (
              <div className="empresa-quick-stat">
                <span className="empresa-quick-stat__label">Revenue Total</span>
                <span className="empresa-quick-stat__value empresa-quick-stat__value--success">
                  {formatCurrency(totalRevenue, 'USD')}
                </span>
              </div>
            )}
            <div className="empresa-quick-stat">
              <span className="empresa-quick-stat__label">Total Cotizado</span>
              <span className="empresa-quick-stat__value">
                {formatCurrency(totalCotizado, 'USD')}
              </span>
            </div>
            <div className="empresa-quick-stat">
              <span className="empresa-quick-stat__label">Cotizaciones Aceptadas</span>
              <span className="empresa-quick-stat__value">
                {cotAceptadas} / {cotizaciones.length}
              </span>
            </div>
            <div className="empresa-quick-stat">
              <span className="empresa-quick-stat__label">Proyectos</span>
              <span className="empresa-quick-stat__value">
                {proyectos.length}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
